import { useId } from "react";
import type { CSSProperties, ReactNode } from "react";
import type { Buyer, BuyerField } from "@libs/buyer-identity-client";
import { diallingCode } from "@libs/buyer-identity-client/phone";
import {
  fieldLabel,
  listedCountries,
  phoneCountries,
  withBuyerCountry,
  withBuyerPhone,
} from "@libs/buyer-identity-client/requirements";
import type { BuyerFieldDefinition } from "@libs/buyer-identity-client/requirements";
export type { BuyerFieldDefinition } from "@libs/buyer-identity-client/requirements";
export const buyerFields: BuyerFieldDefinition[] = [
  { id: "firstName", label: "First name", autoComplete: "given-name", type: "text" },
  { id: "lastName", label: "Last name", autoComplete: "family-name", type: "text" },
  {
    id: "address",
    label: "Billing address",
    autoComplete: "street-address",
    type: "text",
  },
  {
    id: "postcode",
    label: "Postcode",
    autoComplete: "postal-code",
    type: "text",
  },
  {
    id: "country",
    label: "Country code",
    autoComplete: "country",
    type: "text",
  },
  { id: "phone", label: "Mobile number", autoComplete: "tel", type: "tel" },
];

/** Host-rendered, themeable profile fields. Storage and authentication belong
 * to the identity broker; callers declare the fields their journey requires,
 * as the buyer's country has them (`buyerFieldsFor`): a region chosen from
 * the country's list, a field the buyer may leave empty marked "(optional)",
 * and the phone's country chosen beside its number. */
export function BuyerFields({
  value,
  fields = buyerFields,
  onChange,
  onBlur,
  disabled,
  inputClassName,
  inputStyle,
  labelStyle,
  gap = 16,
  notes,
}: {
  value: Partial<Buyer>;
  fields?: readonly BuyerFieldDefinition[];
  onChange: (value: Partial<Buyer>) => void;
  /** A typed field was left (its input lost focus). */
  onBlur?: (field: BuyerFieldDefinition["id"]) => void;
  disabled?: boolean;
  inputClassName?: string;
  /** A host's own field look (a checkout's tokens), instead of a class. */
  inputStyle?: CSSProperties;
  labelStyle?: CSSProperties;
  gap?: number | string;
  /** Said under a field, apart from its label (a country the checkout
   *  cannot deliver to, a postcode that is not one of the country's). */
  notes?: Partial<Record<BuyerField, ReactNode>>;
}) {
  const ids = useId();
  // The neutral field look, where the host gives none.
  const look: CSSProperties | undefined =
    inputStyle ??
    (!inputClassName
      ? {
          padding: "12px",
          borderRadius: 8,
          border: "1px solid currentColor",
          color: "inherit",
          background: "transparent",
          font: "inherit",
        }
      : undefined);
  return (
    <div style={{ display: "grid", gap }}>
      {fields.map((field) => {
        const note = notes?.[field.id];
        const label = fieldLabel(field);
        const control =
          field.type === "tel" && field.withCountry ? (
            // The number's country beside it, chosen apart from the address's:
            // a phone from elsewhere is dialled in its own country.
            <div style={{ display: "grid", gap: 6, fontSize: 14, ...labelStyle }}>
              <span id={`${ids}-${field.id}`}>{label}</span>
              <div style={{ display: "flex", gap: 8, minWidth: 0 }}>
                <span
                  className={inputClassName}
                  style={{
                    ...look,
                    alignItems: "center",
                    display: "inline-flex",
                    flex: "0 0 auto",
                    justifyContent: "center",
                    minWidth: 72,
                    position: "relative",
                    // Beside the number, never a host's full-width field.
                    width: "auto",
                  }}
                >
                  <span aria-hidden="true">{diallingCode(value.phoneCountry) ?? "+"}</span>
                  <select
                    aria-label="Country code"
                    value={value.phoneCountry ?? ""}
                    disabled={disabled}
                    onChange={(e) => onChange({ ...value, phoneCountry: e.target.value })}
                    style={{
                      cursor: "pointer",
                      font: "inherit",
                      height: "100%",
                      inset: 0,
                      opacity: 0,
                      position: "absolute",
                      width: "100%",
                    }}
                  >
                    <option value="" disabled>
                      Select country
                    </option>
                    {phoneCountries.map((country) => (
                      <option key={country.code} value={country.code}>
                        {country.name} ({diallingCode(country.code)})
                      </option>
                    ))}
                  </select>
                </span>
                <input
                  aria-labelledby={`${ids}-${field.id}`}
                  className={inputClassName}
                  style={{ ...look, flex: "1 1 0%", minWidth: 0, width: "auto" }}
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel-national"
                  required
                  maxLength={240}
                  value={value.phone ?? ""}
                  disabled={disabled}
                  onBlur={() => onBlur?.(field.id)}
                  onChange={(e) => onChange(withBuyerPhone(value, e.target.value))}
                />
              </div>
            </div>
          ) : (
            <label
              style={{ display: "grid", gap: 6, fontSize: 14, ...labelStyle }}
            >
              <span>{label}</span>
              {field.type === "choice" ? (
                <select
                  className={inputClassName}
                  style={inputStyle}
                  value={value[field.id] ?? ""}
                  disabled={disabled}
                  onChange={(e) => onChange({ ...value, [field.id]: e.target.value })}
                >
                  <option value="" disabled>
                    Select
                  </option>
                  {(field.allowedValues ?? []).map((choice) => (
                    <option key={choice} value={choice}>
                      {choice}
                    </option>
                  ))}
                </select>
              ) : field.type === "region" ? (
                <select
                  className={inputClassName}
                  style={inputStyle}
                  autoComplete={field.autoComplete}
                  value={value.region ?? ""}
                  disabled={disabled}
                  onChange={(e) =>
                    onChange({ ...value, region: e.target.value || undefined })
                  }
                >
                  {/* One the buyer may leave empty can go back to none. */}
                  <option value="" disabled={!field.optional}>
                    {field.optional ? "None" : `Select ${field.label.toLowerCase()}`}
                  </option>
                  {(field.options ?? []).map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : field.type === "country" ? (
                <select
                  className={inputClassName}
                  style={inputStyle}
                  value={value[field.id] ?? ""}
                  disabled={disabled}
                  // The address country also re-reads the region and phone;
                  // any other country field (a nationality) is only itself.
                  onChange={(e) => onChange(field.id === "country" ? withBuyerCountry(value, e.target.value) : { ...value, [field.id]: e.target.value })}
                >
                  <option value="" disabled>
                    Select country
                  </option>
                  {listedCountries(field).map((country) => (
                    <option key={country.code} value={country.code}>
                      {country.name}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  className={inputClassName}
                  style={look}
                  type={field.type}
                  autoComplete={field.autoComplete}
                  required={!field.optional}
                  maxLength={field.id === "country" ? 2 : 240}
                  value={value[field.id] ?? ""}
                  disabled={disabled}
                  onBlur={() => onBlur?.(field.id)}
                  onChange={(e) =>
                    onChange({
                      ...value,
                      [field.id]:
                        field.id === "country"
                          ? e.target.value.toUpperCase()
                          : e.target.value,
                    })
                  }
                />
              )}
            </label>
          );
        // The same element with a note or without, so a field typed in keeps
        // its focus as a note under it comes and goes.
        return (
          <div key={field.id} style={{ display: "grid", gap: 6 }}>
            {control}
            {note}
          </div>
        );
      })}
    </div>
  );
}
